import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';
import { Shield, UserCheck, Users, Loader2 } from 'lucide-react';

export function MyTeamPage() {
  const { data: teamData, isLoading } = useQuery({
    queryKey: ['my-team'],
    queryFn: async () => {
      const res = await apiClient.get('/profile/my-team');
      return res.data.data;
    }
  });

  if (isLoading) {
    return (
      <Layout title="My Team">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!teamData) {
    return (
      <Layout title="My Team">
        <div className="text-center py-12 text-slate-500">
          Failed to load team data.
        </div>
      </Layout>
    );
  }

  const { me, groups } = teamData;

  return (
    <Layout title="My Team & Reporting">
      <div className="max-w-5xl mx-auto space-y-6 mb-12">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight font-display">Team Structure</h1>
          <p className="text-sm text-slate-500 mt-1">View your reporting hierarchy across different department groups.</p>
        </div>

        {groups && groups.length > 0 ? groups.map((group: any) => {
          const { groupName, leaders, heads, members } = group;
          return (
            <div key={groupName} className="p-6 bg-white border border-slate-200 shadow-sm rounded-2xl space-y-8 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-blue-600"></div>
              
              <div className="border-b border-slate-100 pb-4">
                <h2 className="text-xl font-black text-slate-800 font-display">{groupName}</h2>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-semibold">Department Group Hierarchy</p>
              </div>
              
              {/* GROUP LEADERS SECTION */}
              <div>
                <div className="flex items-center mb-4">
                  <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider flex items-center gap-2">
                    <Shield className="w-4 h-4 text-indigo-600" /> Group Leaders
                  </h4>
                </div>

                {leaders.length > 0 ? (
                  <div className="space-y-3">
                    {leaders.map((leader: any) => (
                      <div key={leader.id} className="bg-white border border-indigo-100 rounded-xl p-4 shadow-sm flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-lg">
                            {leader.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800">{leader.name} {leader.id === me.id && <span className="ml-2 text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-bold">YOU</span>}</div>
                            <div className="text-xs text-slate-500">{leader.email}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-slate-200 bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-xs font-medium text-slate-500">No Leader assigned.</p>
                  </div>
                )}
              </div>

              {/* COMMITTEE HEADS SECTION */}
              <div>
                <div className="flex items-center mb-4">
                  <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-emerald-600" /> Committee Heads
                  </h4>
                </div>

                {heads.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {heads.map((head: any) => (
                      <div key={head.id} className="bg-white border border-emerald-100 rounded-xl p-4 shadow-sm flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-lg">
                            {head.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800">{head.name} {head.id === me.id && <span className="ml-2 text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-bold">YOU</span>}</div>
                            <div className="text-xs text-slate-500">{head.email}</div>
                            {head.managerName && (
                              <div className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">
                                Reports to: <span className="text-slate-600">{head.managerName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-slate-200 bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-xs font-medium text-slate-500">No Committee Heads assigned.</p>
                  </div>
                )}
              </div>

              {/* COMMITTEE MEMBERS SECTION */}
              <div>
                <div className="flex items-center mb-4">
                  <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" /> Committee Members
                  </h4>
                </div>

                {members.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {members.map((member: any) => (
                      <div key={member.id} className="bg-white border border-blue-100 rounded-xl p-4 shadow-sm flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                            {member.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800 text-sm">{member.name} {member.id === me.id && <span className="ml-1.5 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-bold">YOU</span>}</div>
                            <div className="text-[11px] text-slate-500">{member.email}</div>
                            <div className="text-[10px] font-bold text-slate-400 mt-0.5 uppercase tracking-wider flex items-center gap-1.5 flex-wrap">
                              {member.managerName && <span>Reports to: <span className="text-slate-600">{member.managerName}</span></span>}
                              {member.subCoreRole && (
                                <span className="bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded text-[9px] font-black">
                                  {member.subCoreRole.replace(/_/g, ' ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-slate-200 bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-xs font-medium text-slate-500">No Committee Members assigned.</p>
                  </div>
                )}
              </div>

            </div>
          );
        }) : (
          <div className="p-12 bg-white border border-slate-200 shadow-sm rounded-2xl text-center">
            <h3 className="text-lg font-bold text-slate-800">No Committees Mapped</h3>
            <p className="text-sm text-slate-500 mt-2">You are not currently mapped to any department group.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
